/**
 * The combined model picker's data: every ready instance's models as one
 * list in sections, and the selected model's Reasoning and Speed choices as
 * segments.
 *
 * @module components/chat/ModelEffortPicker.logic
 */
import type { ProviderInstanceId, ProviderOptionDescriptor } from "@amoreg/contracts";
import { getProviderOptionCurrentValue } from "@amoreg/shared/model";
import { AGENT_SERVICE_TIER_OPTION_ID, type AgentServiceTier } from "@repo/shared/agentModels";

import { isProviderInstancePickerReady, type ProviderInstanceEntry } from "../../providerInstances";
import { modelPickerModelKey } from "./modelPickerKeys";

export type SelectDescriptor = Extract<ProviderOptionDescriptor, { type: "select" }>;

/** Speed always draws these three, so a model without one eases the others wider instead of leaving a gap. */
const SPEED_TIERS: ReadonlyArray<{ readonly id: AgentServiceTier; readonly label: string }> = [
  { id: "default", label: "Standard" },
  { id: "fast", label: "Fast" },
  { id: "ultrafast", label: "Ultrafast" },
];

/** One line each: when to reach for the level, not how it works. */
const REASONING_HINT: Readonly<Record<string, string>> = {
  Low: "Quick answers and simple lookups",
  Medium: "Everyday questions and drafting",
  High: "Hard questions that need the deepest thinking",
};

export interface EffortSegment {
  readonly id: string;
  readonly label: string;
  /** The segment's tooltip. */
  readonly title: string;
  /** A speed tier this model lacks: drawn folded to nothing. */
  readonly absent: boolean;
}

export interface SpeedSegment extends EffortSegment {
  readonly tier: AgentServiceTier;
}

export const selectValue = (descriptor: SelectDescriptor | null): string | null => {
  const value = descriptor ? getProviderOptionCurrentValue(descriptor) : null;
  return typeof value === "string" ? value : null;
};

/** The model's selects: Speed is the service tier, Reasoning the first other select. */
export function effortSelects(descriptors: ReadonlyArray<ProviderOptionDescriptor>): {
  readonly reasoning: SelectDescriptor | null;
  readonly speed: SelectDescriptor | null;
} {
  const selects = descriptors.filter((descriptor): descriptor is SelectDescriptor => descriptor.type === "select");
  return {
    reasoning: selects.find((descriptor) => descriptor.id !== AGENT_SERVICE_TIER_OPTION_ID) ?? null,
    speed: selects.find((descriptor) => descriptor.id === AGENT_SERVICE_TIER_OPTION_ID) ?? null,
  };
}

export const reasoningSegments = (descriptor: SelectDescriptor): ReadonlyArray<EffortSegment> =>
  descriptor.options.map((option) => ({
    id: option.id,
    label: option.label,
    title: REASONING_HINT[option.label] ?? option.label,
    absent: false,
  }));

/** Speed says only what the choice costs; its name is already on the segment. */
export const speedSegments = (descriptor: SelectDescriptor): ReadonlyArray<SpeedSegment> =>
  SPEED_TIERS.map(({ id, label }) => {
    const option = descriptor.options.find((candidate) => candidate.id === id);
    return option
      ? { id: option.id, tier: id, label: option.label, title: option.description ?? "1× usage", absent: false }
      : { id: `absent:${id}`, tier: id, label, title: label, absent: true };
  });

export interface ModelEsqueRow<Model> {
  readonly key: string;
  readonly model: Model;
  /** Position in the whole list, for the combobox's keyboard walk. */
  readonly index: number;
  readonly disabledReason: string | null;
}

export interface ModelSection<Model> {
  readonly entry: ProviderInstanceEntry;
  readonly rows: ReadonlyArray<ModelEsqueRow<Model>>;
}

/** Every ready instance's models, in instance order; a legacy model only while it is the selected one. */
export function buildModelSections<Model extends { readonly slug: string; readonly isLegacy?: boolean | undefined }>(picker: {
  readonly instanceEntries: ReadonlyArray<ProviderInstanceEntry>;
  readonly modelOptionsByInstance: ReadonlyMap<ProviderInstanceId, ReadonlyArray<Model>>;
  readonly activeInstanceId: ProviderInstanceId;
  readonly model: string;
  readonly lockedProvider: ProviderInstanceEntry["driverKind"] | null;
  readonly lockedContinuationGroupKey?: string | null | undefined;
  readonly getModelDisabledReason?: ((instanceId: ProviderInstanceId, model: string) => string | null) | undefined;
}): ReadonlyArray<ModelSection<Model>> {
  const sections: ModelSection<Model>[] = [];
  let index = 0;
  for (const entry of picker.instanceEntries) {
    if (!isProviderInstancePickerReady(entry)) continue;
    const locked =
      picker.lockedProvider !== null &&
      (entry.driverKind !== picker.lockedProvider ||
        (Boolean(picker.lockedContinuationGroupKey) && entry.continuationGroupKey !== picker.lockedContinuationGroupKey));
    const rows = (picker.modelOptionsByInstance.get(entry.instanceId) ?? [])
      .filter((model) => !model.isLegacy || (entry.instanceId === picker.activeInstanceId && model.slug === picker.model))
      .map((model) => ({
        key: modelPickerModelKey(entry.instanceId, model.slug),
        model,
        index: index++,
        disabledReason: locked
          ? `${entry.displayName} is unavailable in this thread. Start a new thread to switch providers.`
          : (picker.getModelDisabledReason?.(entry.instanceId, model.slug) ?? null),
      }));
    if (rows.length > 0) sections.push({ entry, rows });
  }
  return sections;
}
